import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ProcessMenu } from '../components/ProcessMenu.js';
import { PaymentOptionsNav } from '../components/PaymentOptionsNav.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { EmailTemplateMenu } from '../email-templates/EmailTemplateMenu.js';
import { DIRECT_DEBITS_TEMPLATES } from '../email-templates/templates.js';
import { useContentMap } from '../content.js';
import { resolveActionBlockMessage } from '../lib/action-denied.js';
import { accountOptionLabel } from '../lib/account-label.js';
import {
  requiresMultipleSignatories,
  MULTIPLE_SIGNATORIES_MESSAGE,
} from '../lib/signatories.js';

/**
 * SCRN 33 — Direct Debits Overview (CCF-71) + SCRN 34 — Direct Debit
 * Enquiry / Refusal / Cancel (CCF-71 / CCF-102).
 *
 * Faithful port of the customer's Payments-homescreen Figma (the Direct Debits
 * flow). The agent selects an account and sees the account's Direct Debits in a
 * table (Institution Name + Reference). Per the supervisor's UX refinement
 * (#255): the agent CLICKS a row directly — no tick-box / "View selected" step —
 * to open the Direct Debit Information + Refusal Information detail panel below
 * the table, with the servicing action:
 *   - Amend / remove refusals (CCF-71) — "Number of payments to be refused"
 *     (mandatory, max 2 digits, max value 99; 0 removes refusals) + a Confirm
 *     button that surfaces the customer-confirmation scripting and, on a real
 *     gateway success, re-reads the detail so the "Number of Refusals Requested"
 *     and "Last Refusal Date" auto-update (CCF-71).
 *
 * Each listing row also has a trailing trashcan-icon column that starts the
 * cancel-Direct-Debit process (#255 — supersedes the old "Cancel this Direct
 * Debit" button). Clicking it opens a confirmation dialog (mirrors the Bill
 * Payments / Standing Orders row-trashcan dialogs, #269/#267) rather than
 * cancelling on the first click.
 *
 * Backed by the Teller-ID + RBAC + verification-gated BFF via the generated typed
 * client, matching the sibling Standing Orders / Bill Payments servicing screens:
 *   - accounts: POST /api/customers/accounts
 *   - listing:  POST /api/payments/direct-debit-list      (Payment Type "DD")
 *   - detail:   POST /api/payments/direct-debit-details    (keyed on PaymentsSeq)
 *   - refusal:  POST /api/payments/direct-debit-refusal    (CCF-71 amend/remove)
 *   - cancel:   POST /api/payments/direct-debit-cancel     (CCF-102 — #336, the
 *               SAME shared gateway `payment/bill-cancel` op the bill-payment/
 *               standing-order cancels use, PaymentType "D")
 *
 * CCF-102 cancel was previously DEFERRED (issue #219 pt4) because the DD
 * delete/cancellation endpoint was assumed missing. #336 found it IS buildable
 * now: the R4 `payment/bill-cancel` op is generic across payment types via its
 * `PaymentType` field ("B"/"D"/"S"), so the trashcan now cancels for real via
 * the BFF/gateway, identified by the row's `PaymentsSeq` alone (`PaymentsNo` is
 * mandatory only for PaymentType "B").
 *
 * The shared agent banner lives in the app shell, so this screen renders only the
 * `<main>` content.
 */

/**
 * Maximum Direct Debit rows shown per page in the overview list (#481 — "List
 * lengths": long lists get a page-size cap + Next/Previous). Pagination is
 * client-side over the already-loaded list, mirroring the Standing Orders /
 * Bill Payments overview pattern.
 */
const DIRECT_DEBITS_PER_PAGE = 10;

interface AccountRow {
  AccountNumber: string;
  AccountType: string;
  Nickname?: string;
  /**
   * #799 — the per-account signatory count the BFF normalises from the gateway
   * `customer/accounts/list` `NumofSignatories` field. When it is > 1 the
   * Direct Debit CANCEL controls are suppressed ("two to sign" mandate); read
   * via the shared `requiresMultipleSignatories` helper.
   */
  Signatories?: number | string;
}

/** A Direct Debit listing row — Payment Type "DD" (CCF-71). */
interface DirectDebitRow {
  PaymentId?: string;
  /** Lifecycle status (ACTIVE / CANCELLED) — the SCRN-33 "Status" column (#558/#559). */
  Status?: string;
  InstitutionName?: string;
  Reference?: string;
  AccountToBeDebited?: string;
  LastPresentedDate?: string;
  /** Unibanks key — threaded to the detail/refusal ops (CCF-71). */
  PaymentsSeq?: string;
  /** Authority number — REQUIRED by the gateway on the DD cancel (#393). */
  PaymentsNo?: string;
}

/** Full Direct Debit detail + refusal information (SCRN-34 / CCF-71). */
interface DirectDebitDetail {
  Found?: boolean;
  AccountNumber?: string;
  InstitutionName?: string;
  Reference?: string;
  AccountToBeDebited?: string;
  OriginatorNumber?: string;
  LastPresentedDate?: string;
  LastRefusalDate?: string;
  NumOfRefusalsRequested?: string;
  NumOfPaymentsToBeRefused?: string;
  PaymentsSeq?: string;
  /** Authority number — threaded to the DD cancel when present (#393). */
  PaymentsNo?: string;
  /** Carried on a not-found / failed detail response for inline UX (#103). */
  ResultCode?: string;
  ResultMessage?: string;
}

/**
 * Build a concise inline error string from a gateway not-found / failed detail
 * response, surfacing the real Code + Message beside the action (#103). Falls
 * back to a generic, recoverable message when the gateway gave neither.
 */
function detailErrorText(d?: {
  ResultCode?: string;
  ResultMessage?: string;
}): string {
  const code = (d?.ResultCode ?? '').trim();
  const message = (d?.ResultMessage ?? '').trim();
  if (code && message) return `Could not load details — ${message} (${code}).`;
  if (message) return `Could not load details — ${message}.`;
  if (code) return `Could not load details — gateway code ${code}.`;
  return 'Could not load Direct Debit details. Please retry.';
}

/**
 * Build the inline refusal-write failure string from the gateway's write outcome
 * (Success:false + ResultCode/ResultMessage), surfacing the real Code + Message
 * beside the Confirm button (recoverable) rather than only a fleeting toast
 * (@dylan219 #80/#82/#94; #141).
 */
function refusalErrorText(
  httpStatus: number | undefined,
  d?: { ResultCode?: string; ResultMessage?: string },
): string {
  const code = (d?.ResultCode ?? '').trim();
  const message = (d?.ResultMessage ?? '').trim();
  if (code && message)
    return `Could not amend the refusal — ${message} (${code}).`;
  if (message) return `Could not amend the refusal — ${message}.`;
  if (code) return `Could not amend the refusal — gateway code ${code}.`;
  if (httpStatus && httpStatus >= 400)
    return `Could not amend the refusal — request failed (${httpStatus}). Please retry.`;
  return 'Could not amend the refusal. Please retry.';
}

/**
 * Build the inline cancel-write failure string from the gateway's write outcome
 * (Success:false + ResultCode/ResultMessage), surfacing the real Code + Message
 * beside the Confirm cancellation action (recoverable) — the same convention as
 * {@link refusalErrorText} (@dylan219 #80/#82/#94; #141).
 */
function cancelErrorText(
  httpStatus: number | undefined,
  d?: { ResultCode?: string; ResultMessage?: string },
): string {
  const code = (d?.ResultCode ?? '').trim();
  const message = (d?.ResultMessage ?? '').trim();
  if (code && message)
    return `Could not cancel the Direct Debit — ${message} (${code}).`;
  if (message) return `Could not cancel the Direct Debit — ${message}.`;
  if (code) return `Could not cancel the Direct Debit — gateway code ${code}.`;
  if (httpStatus && httpStatus >= 400)
    return `Could not cancel the Direct Debit — request failed (${httpStatus}). Please retry.`;
  return 'Could not cancel the Direct Debit. Please retry.';
}

/**
 * Human display label for a gateway Direct-Debit status (#558/#559). Normalises +
 * maps the known families onto their tidy label — ACTIVE → "Active", the CANCEL*
 * family → "Cancelled" (mirrors the standing-order `soStatusLabel`). Any unmapped
 * status falls back to the raw value so a new gateway status still surfaces; an
 * empty/absent status defaults to "Active" (the pre-#558 rows were all active).
 */
function ddStatusLabel(status?: string): string {
  const s = (status ?? '').trim().toUpperCase();
  if (s === '') return 'Active';
  if (s.startsWith('CANCEL')) return 'Cancelled';
  if (s === 'ACTIVE') return 'Active';
  return status ?? '';
}

/**
 * Whether a Direct Debit listing row is already cancelled/deleted (#622). Uses
 * the SAME CANCEL* status family that {@link ddStatusLabel} renders as
 * "Cancelled" (#558/#559). An already-cancelled Direct Debit must NOT expose the
 * trashcan/delete affordance — the agent should never be able to ATTEMPT to
 * cancel a DD the gateway has already cancelled (project convention #529/#530:
 * prevent the ineligible action at the app layer, don't rely on the gateway
 * rejecting it).
 */
function isDirectDebitCancelled(status?: string): boolean {
  return (status ?? '').trim().toUpperCase().startsWith('CANCEL');
}

/** Accessible toggle switch, faithful to the export's "Show all" toggle (shared
 *  markup with the Standing Orders overview toggle, #558/#559). */
function Toggle({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: () => void;
}): React.JSX.Element {
  return (
    <div className="flex items-center gap-3">
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
        className="sr-only peer"
        aria-checked={checked}
      />
      <label
        htmlFor={id}
        className="relative flex items-center cursor-pointer group"
      >
        <div
          className={`relative w-[44px] h-[24px] rounded-full transition-all ${
            checked ? 'bg-[#FF703E]' : 'bg-[#d1d5db]'
          } peer-focus-visible:outline peer-focus-visible:outline-4 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-[#005fcc]`}
        >
          <div
            className={`absolute top-[2px] left-[2px] w-[20px] h-[20px] rounded-full bg-white transition-transform ${
              checked ? 'translate-x-[20px]' : 'translate-x-0'
            }`}
          ></div>
        </div>
        <span className="ml-3 text-[14px] font-['Arial',sans-serif] text-[#6b7280] select-none">
          {label}
        </span>
      </label>
    </div>
  );
}

/** Accessible dropdown, faithful to the export's listbox pattern (shared with
 *  the sibling servicing screens — see BillPaymentsScreen / ChequebookScreen). */
function Dropdown({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor={id}
        className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <div className="relative">
        <button
          id={id}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span className={selected ? '' : 'text-[#6b7280]'}>
            {selected ? selected.label : placeholder}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            aria-label={label}
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((option) => (
              <li
                key={option.value || 'placeholder'}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(option.value);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === option.value ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** A read-only labelled field used in the detail grids. */
function DetailField({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <>
      <div className="text-[14px] font-['Arial',sans-serif] text-[#6b7280]">
        {label}
      </div>
      <div className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]">
        {value}
      </div>
    </>
  );
}

export function DirectDebitsScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();
  // #637 inc 20 — the resolved content-catalogue map, threaded into the SEPA DD
  // Recall email template so its editable wording (subject/title/section
  // headers/labels/notice/instruction/sign-off) reflects any admin reword;
  // absent-key defaults keep the output unchanged.
  const contentMap = useContentMap();

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  const ownerName = customer.name ?? '';
  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  // Accounts + selection.
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [error, setError] = useState('');
  const [account, setAccount] = useState('');

  // Listing (SCRN-33) state.
  const [directDebits, setDirectDebits] = useState<DirectDebitRow[]>([]);
  // "Show Cancelled" toggle (#558/#559) — when on, the listing refetches with
  // includeInactive so the BFF returns the active DDs COMBINED with the cancelled
  // ones (active first). Mirrors the Standing Orders overview toggle.
  const [includeInactive, setIncludeInactive] = useState(false);
  // Client-side pagination of the loaded Direct Debits list (#481). Reset to 0
  // whenever the list is (re)loaded so a new account always starts on page 1.
  const [page, setPage] = useState(0);
  const [listLoading, setListLoading] = useState(false);
  const [selectedSeq, setSelectedSeq] = useState<string | null>(null);
  const listSeq = useRef(0);
  // #829 (server-side pagination): the gateway returns at most ~100 Direct
  // Debits per call. `hasMore` mirrors the response `hasMore` for the CURRENTLY
  // loaded rows; the cursor is the continuation to fetch the NEXT ~100. When the
  // agent steps to a UI page whose rows aren't loaded yet AND `hasMore`, we
  // lazily re-call direct-debit-list with the cursor and APPEND the next block
  // (page 11 → 2nd call, page 21 → 3rd, …). So Previous/Next can walk past page
  // 10 when there are >100 DDs, without a jump-to-page control (@dylan219
  // 2026-09-24). `others` (two-stream, #558/#559): the active stream is paged
  // first, then the cancelled stream on its own cursor once active is exhausted.
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const cursorRef = useRef<{ seq?: string; no?: string; others?: boolean }>({});

  // Trashcan / cancel-Direct-Debit (#255/#336): the row (if any) whose cancel
  // confirmation dialog is open, its explicit consent tick, in-flight state,
  // inline recoverable failure and the post-cancel success confirmation.
  const [cancelRow, setCancelRow] = useState<DirectDebitRow | null>(null);
  const [cancelRowConfirmed, setCancelRowConfirmed] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelRowError, setCancelRowError] = useState('');
  const [cancelSuccessMessage, setCancelSuccessMessage] = useState('');

  // Detail panel (SCRN-34) state.
  const [detail, setDetail] = useState<DirectDebitDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const detailSeq = useRef(0);

  // Refusal amend (CCF-71) state.
  const [refusalsToBe, setRefusalsToBe] = useState('');
  const [refusalConfirmed, setRefusalConfirmed] = useState(false);
  const [refusalSubmitting, setRefusalSubmitting] = useState(false);
  // Snapshot of the confirmation scripting captured at submit time (#378). The
  // "removed N refusals" count must report the refusals ACTUALLY removed — the
  // count that WAS on the record before the amend — not the newly-entered target
  // (entering 0 to remove refusals otherwise read "removed the 0 refusals").
  // Snapshotting here also keeps the message stable across the post-write detail
  // re-read (which overwrites NumOfRefusalsRequested with the new value).
  const [refusalSummary, setRefusalSummary] = useState<{
    entered: number;
    removedCount: number;
    institutionName: string;
    reference: string;
    accountLast4: string;
  } | null>(null);
  // Inline, recoverable refusal-write failure (gateway Code + Message).
  const [refusalError, setRefusalError] = useState('');

  // Load the verified customer's accounts (Teller-ID gated). On ANY customer /
  // teller context change, SYNCHRONOUSLY clear every customer-scoped piece of
  // screen state and invalidate any in-flight list/detail request BEFORE the new
  // fetch — so the previous customer's Direct Debit rows/detail can never remain
  // visible while (or if) the new fetch resolves/fails (cross-customer data
  // exposure in the browser; server-side ownership still gates writes).
  useEffect(() => {
    let cancelled = false;
    // Invalidate any in-flight list/detail responses from the prior customer.
    listSeq.current += 1;
    detailSeq.current += 1;
    setAccounts([]);
    setAccount('');
    setDirectDebits([]);
    setIncludeInactive(false);
    setListLoading(false);
    setSelectedSeq(null);
    setDetail(null);
    setDetailError('');
    setDetailLoading(false);
    setRefusalsToBe('');
    setRefusalConfirmed(false);
    setRefusalSummary(null);
    setRefusalError('');
    setCancelRow(null);
    setCancelRowConfirmed(false);
    setCancelSubmitting(false);
    setCancelRowError('');
    setCancelSuccessMessage('');
    setError('');
    async function load(): Promise<void> {
      if (!hasContext) {
        setError('No verified customer in context. Complete ID&V first.');
        return;
      }
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/accounts',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        if (apiError || !data) {
          setError('Could not load accounts.');
          return;
        }
        setAccounts((data.Accounts ?? []) as unknown as AccountRow[]);
      } catch {
        if (!cancelled) setError('Request failed.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  const accountOptions = accounts.map((a) => ({
    value: a.AccountNumber,
    label: accountOptionLabel(a),
  }));

  // #799 (@dylan219, increment 3) — Direct Debit CANCELLATION is not permitted on
  // an account that requires MORE THAN ONE signatory ("two to sign" mandate); the
  // change has to go through the mandate. The signal is the BFF-normalised
  // per-account `Signatories` count (from the gateway `NumofSignatories`), read
  // via the SHARED helper so all six blocked action-areas apply the identical
  // rule. Blocks only on a KNOWN count > 1. The per-row trashcan is disabled +
  // the verbatim block message shown inline; the BFF
  // `/payments/direct-debit-cancel` route enforces the SAME block server-side
  // (#173), so a reload / direct call cannot bypass it. Refusal amend is a
  // SEPARATE action and deliberately unaffected.
  const accountBlocksCancel = requiresMultipleSignatories(
    accounts.find((a) => a.AccountNumber === account),
  );

  /** Clear the detail panel + its edit buffer (SCRN-34). */
  function resetDetail(): void {
    setSelectedSeq(null);
    setDetail(null);
    setDetailError('');
    setRefusalsToBe('');
    setRefusalConfirmed(false);
    setRefusalSummary(null);
    setRefusalError('');
  }

  /**
   * Open the cancel-Direct-Debit confirmation dialog for a listing row (#255 —
   * the trashcan icon supersedes the old "Cancel this Direct Debit" button).
   * Cancelling does NOT fire on the first click — the agent must review the
   * Direct Debit with the customer and tick the explicit consent in the dialog
   * (parity with the sibling Bill Payments / Standing Orders row-trashcan
   * dialogs, #269/#267 — a state-changing banking action can never fire on a
   * single click).
   */
  function openCancelDialog(row: DirectDebitRow): void {
    // #799 — never open the cancel-confirm dialog for a >1-signatory account
    // (the trashcan is disabled, but guard here too so it can never fire).
    if (accountBlocksCancel) return;
    setCancelRow(row);
    setCancelRowConfirmed(false);
    setCancelRowError('');
    setCancelSuccessMessage('');
  }

  /** Dismiss the cancel confirmation dialog without cancelling. */
  function closeCancelDialog(): void {
    setCancelRow(null);
    setCancelRowConfirmed(false);
    setCancelRowError('');
  }

  /**
   * Confirm the Direct Debit cancellation (SCRN 34 / CCF-102, #336). Posts the
   * BFF `/payments/direct-debit-cancel` op (RBAC + Teller-ID + verification
   * gated, mapping to the shared gateway `payment/bill-cancel` op with
   * PaymentType "D"), identified by the row's `PaymentsSeq` alone. On a REAL
   * gateway success it closes the dialog, surfaces a positive confirmation +
   * toast, refreshes the listing (so the cancelled DD drops off, CCF-71 has no
   * "show cancelled" toggle) and clears any open detail panel for that row. A
   * gateway-side failure — or an RBAC action denial (#248/#249, e.g. a
   * restricted role cancelling on an OFFLINE journey) — surfaces the Code +
   * Message inline beside the dialog's Confirm button (recoverable), never a
   * false success (#141).
   */
  async function confirmCancelDirectDebit(row: DirectDebitRow): Promise<void> {
    const paymentsSeq = row.PaymentsSeq ?? row.PaymentId ?? '';
    if (!paymentsSeq) {
      const message =
        'This Direct Debit cannot be cancelled (missing reference).';
      setCancelRowError(message);
      toast.error(message);
      return;
    }
    // #393: the real gateway REQUIRES a non-empty PaymentsNo on the DD cancel
    // (omitting it → Error 10006 "The PaymentsNo field is invalid."). Thread the
    // listing row's PaymentsNo; the BFF defaults to "000" (the observed DD value)
    // when a row lacked one, so the send is never empty.
    const paymentsNo = row.PaymentsNo ?? '';
    setCancelSubmitting(true);
    setCancelRowError('');
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/direct-debit-cancel', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: account,
          paymentsSeq,
          ...(paymentsNo ? { paymentsNo } : {}),
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Issue #248 — the RBAC *action* denial: `requirePermission` refuses a
        // blocked write with 403 `{ error: 'forbidden' }` (e.g. a restricted
        // role attempting this cancel on an OFFLINE journey). Surface
        // @dylan219's authoritative verbatim copy inline, never a raw 403.
        const inline =
          resolveActionBlockMessage(response?.status, apiError) ??
          cancelErrorText(
            response?.status,
            data as { ResultCode?: string; ResultMessage?: string } | undefined,
          );
        setCancelRowError(inline);
        toast.error(inline);
        return;
      }
      setCancelRow(null);
      setCancelRowConfirmed(false);
      const message = `Direct Debit${row.InstitutionName ? ` with ${row.InstitutionName}` : ''} cancelled successfully.`;
      setCancelSuccessMessage(message);
      toast.success(message);
      // Clear a stale open detail panel for the row just cancelled.
      if (selectedSeq === paymentsSeq) resetDetail();
      await loadDirectDebits(account);
    } catch {
      const message = 'Could not cancel the Direct Debit. Please retry.';
      setCancelRowError(message);
      toast.error(message);
    } finally {
      setCancelSubmitting(false);
    }
  }

  /**
   * Load the account's Direct Debits (CCF-71). `inactive` selects the
   * "Show Cancelled" view (#558/#559): the BFF returns the active DDs COMBINED
   * with the cancelled ones (active on top, cancelled appended below). The
   * default shows only active Direct Debits.
   */
  async function loadDirectDebits(
    accountNumber: string,
    inactive = includeInactive,
  ): Promise<void> {
    if (!accountNumber) return;
    const seq = ++listSeq.current;
    setListLoading(true);
    setError('');
    resetDetail();
    // First page only — the next ~100-row block is fetched lazily by `loadMore`
    // when the agent steps past the loaded rows (#829).
    cursorRef.current = {};
    setHasMore(false);
    try {
      const { data, error: apiError } = await api.POST(
        '/payments/direct-debit-list',
        {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: {
            customerNumber,
            accountNumber,
            ...(inactive ? { includeInactive: true } : {}),
          },
        },
      );
      if (seq !== listSeq.current) return; // a newer request superseded this one.
      if (apiError || !data) {
        setDirectDebits([]);
        setError('Could not load Direct Debits.');
        return;
      }
      setDirectDebits((data.Payments ?? []) as unknown as DirectDebitRow[]);
      setHasMore(data.hasMore ?? false);
      cursorRef.current = {
        seq: data.nextPaymentsSeq,
        no: data.nextPaymentsNo,
        others: data.othersStream ?? false,
      };
      setPage(0); // a freshly-loaded list always starts on page 1 (#481).
    } catch {
      if (seq === listSeq.current) {
        setDirectDebits([]);
        setError('Request failed.');
      }
    } finally {
      if (seq === listSeq.current) setListLoading(false);
    }
  }

  /**
   * #829: fetch the NEXT ~100-DD block from the gateway using the continuation
   * cursor and APPEND it to the loaded rows. Called when the agent steps to a UI
   * page whose rows aren't loaded yet and `hasMore` is true. The `others` flag
   * (two-stream, #558/#559) is threaded back so the BFF pages the cancelled
   * stream once the active stream is exhausted. Guards against a concurrent fetch
   * (`loadingMore`) and a missing cursor. On failure the loaded rows are left
   * intact and `hasMore` is cleared so "Next" settles on the last loaded page
   * rather than retrying forever; the agent can retry by re-selecting the account.
   */
  async function loadMore(): Promise<boolean> {
    if (loadingMore || !hasMore) return false;
    const cursor = cursorRef.current;
    if (!cursor.seq && !cursor.no) return false;
    setLoadingMore(true);
    try {
      const { data, error: apiError } = await api.POST(
        '/payments/direct-debit-list',
        {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: {
            customerNumber,
            accountNumber: account,
            ...(includeInactive ? { includeInactive: true } : {}),
            ...(cursor.seq ? { nextPaymentsSeq: cursor.seq } : {}),
            ...(cursor.no ? { nextPaymentsNo: cursor.no } : {}),
            ...(cursor.others ? { othersStream: true } : {}),
          },
        },
      );
      if (apiError || !data) {
        setHasMore(false);
        return false;
      }
      const more = (data.Payments ?? []) as unknown as DirectDebitRow[];
      setDirectDebits((cur) => [...cur, ...more]);
      setHasMore(data.hasMore ?? false);
      cursorRef.current = {
        seq: data.nextPaymentsSeq,
        no: data.nextPaymentsNo,
        others: data.othersStream ?? false,
      };
      return more.length > 0;
    } catch {
      setHasMore(false);
      return false;
    } finally {
      setLoadingMore(false);
    }
  }

  /**
   * Step to the next UI page. If the next page's rows aren't loaded yet and the
   * gateway signals more, fetch the next ~100-row block first, then advance
   * (#829). "Next" is disabled only on the last loaded page when `hasMore` is
   * false (handled in the render).
   */
  async function goToNextPage(): Promise<void> {
    const nextStart = (page + 1) * DIRECT_DEBITS_PER_PAGE;
    if (nextStart >= directDebits.length && hasMore) {
      await loadMore();
    }
    setPage((p) => p + 1);
  }

  /** Reset everything below the table when the account changes. */
  function onAccountChange(value: string): void {
    setAccount(value);
    setDirectDebits([]);
    // A fresh account starts with the cancelled DDs hidden (#558/#559).
    setIncludeInactive(false);
    resetDetail();
    closeCancelDialog();
    setCancelSuccessMessage('');
    void loadDirectDebits(value, false);
  }

  /** Toggle the "Show Cancelled" filter and refetch (#558/#559). */
  function onToggleInactive(): void {
    const next = !includeInactive;
    setIncludeInactive(next);
    if (account) void loadDirectDebits(account, next);
  }

  /** Load the selected Direct Debit's full detail + refusal info (CCF-71). */
  async function openDetail(paymentsSeq: string): Promise<void> {
    const seq = ++detailSeq.current;
    setDetailLoading(true);
    setDetailError('');
    setDetail(null);
    setRefusalsToBe('');
    setRefusalConfirmed(false);
    setRefusalSummary(null);
    setRefusalError('');
    try {
      const { data, error: apiError } = await api.POST(
        '/payments/direct-debit-details',
        {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: {
            customerNumber,
            accountNumber: account,
            paymentsSeq,
          },
        },
      );
      if (seq !== detailSeq.current) return;
      if (apiError || !data || (data as DirectDebitDetail).Found !== true) {
        setDetailError(detailErrorText(data as DirectDebitDetail | undefined));
        return;
      }
      setDetail(data as unknown as DirectDebitDetail);
    } catch {
      if (seq === detailSeq.current)
        setDetailError('Could not load Direct Debit details. Please retry.');
    } finally {
      if (seq === detailSeq.current) setDetailLoading(false);
    }
  }

  /**
   * Confirm a refusal amend/remove (CCF-71). Validates the input (0–99; 0
   * removes refusals), submits to the BFF direct-debit-refusal op, and — on a
   * REAL gateway success — surfaces the confirmation scripting and re-reads the
   * detail so the "Number of Refusals Requested" + "Last Refusal Date"
   * auto-update. A gateway-side failure surfaces the Code + Message inline beside
   * the Confirm button (recoverable), never a false success (#141).
   */
  async function confirmRefusal(): Promise<void> {
    if (!detail?.PaymentsSeq) return;
    const n = Number(refusalsToBe);
    if (refusalsToBe === '' || !Number.isInteger(n) || n < 0 || n > 99) {
      toast.error(
        'Enter the number of payments to be refused (0–99; 0 removes refusals).',
      );
      return;
    }
    // Normalise to the canonical count string (drops any leading zero, e.g.
    // "03" → "3", "00" → "0") so it satisfies the BFF contract's 0–99 pattern
    // (^([0-9]|[1-9][0-9])$) rather than tripping runtime validation with a 400.
    const normalised = String(n);
    setRefusalSubmitting(true);
    setRefusalError('');
    setRefusalConfirmed(false);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/payments/direct-debit-refusal', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: account,
          paymentsSeq: detail.PaymentsSeq,
          numOfPaymentsToBeRefused: normalised,
        },
      });
      if (
        apiError ||
        !data ||
        (data as { Success?: boolean }).Success !== true
      ) {
        // Issue #248 — the RBAC *action* denial: `requirePermission` refuses a
        // blocked write with 403 `{ error: 'forbidden' }` (e.g. a restricted
        // role attempting this refusal amend on an OFFLINE journey, per
        // increment 1). Surface @dylan219's authoritative verbatim copy inline
        // beside the Confirm button — never a raw 403 or a generic gateway
        // string. Distinct from the gateway business-decline (#80/#82/#94/#141,
        // `refusalErrorText`) and from the Teller-ID gate (`invalid_teller_id`,
        // a different 403 body that `isActionForbidden` deliberately ignores).
        const inline =
          resolveActionBlockMessage(response?.status, apiError) ??
          refusalErrorText(
            response?.status,
            data as { ResultCode?: string; ResultMessage?: string } | undefined,
          );
        setRefusalError(inline);
        toast.error(inline);
        return;
      }
      // #378 — snapshot the confirmation NOW, from the count that was on the
      // record BEFORE this amend (the DD detail hasn't been re-read yet), so the
      // scripting reports the refusals ACTUALLY removed rather than the target
      // the agent typed. The gateway does not return the pre-amend count, so we
      // read it from the loaded detail. `removedCount` = prior − new target,
      // floored at 0 (removing all → the full prior count; a partial reduction →
      // the difference; raising the count → 0 removed).
      const priorCount = Number.parseInt(
        detail.NumOfRefusalsRequested ?? '0',
        10,
      );
      const prior = Number.isFinite(priorCount) ? priorCount : 0;
      setRefusalSummary({
        entered: n,
        removedCount: Math.max(0, prior - n),
        institutionName: detail.InstitutionName ?? '',
        reference: detail.Reference ?? '',
        accountLast4: (detail.AccountToBeDebited ?? '').slice(-4),
      });
      setRefusalConfirmed(true);
      toast.success(
        n === 0
          ? 'Direct Debit refusals removed.'
          : 'Direct Debit refusal amended.',
      );
      // Re-read the detail so the refusal fields auto-update (CCF-71).
      await openDetailKeepScripting(detail.PaymentsSeq);
    } catch {
      const inline = 'Could not amend the refusal. Please retry.';
      setRefusalError(inline);
      toast.error(inline);
    } finally {
      setRefusalSubmitting(false);
    }
  }

  /**
   * Re-read the detail after a successful refusal WITHOUT clearing the
   * confirmation scripting / input (a lighter variant of {@link openDetail} used
   * only for the CCF-71 auto-update).
   */
  async function openDetailKeepScripting(paymentsSeq: string): Promise<void> {
    const seq = ++detailSeq.current;
    try {
      const { data, error: apiError } = await api.POST(
        '/payments/direct-debit-details',
        {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber, accountNumber: account, paymentsSeq },
        },
      );
      if (seq !== detailSeq.current) return;
      if (!apiError && data && (data as DirectDebitDetail).Found === true) {
        setDetail(data as unknown as DirectDebitDetail);
      }
    } catch {
      // A failed re-read leaves the last-known detail on screen; the write
      // already succeeded (the success toast fired), so this is non-fatal.
    }
  }

  const selectedDD = detail;

  // The listing row for the DD whose detail panel is open (#623). The gateway's
  // `direct-debit-details` op returns Found:true for a CANCELLED Direct Debit but
  // with EMPTY InstitutionName / DebitReference / OriginatorNumber (the debiting
  // account + dates ARE populated), whereas the SAME fields ARE present on the
  // `pay-list` row the table renders. So when the detail block omits Institution
  // Name / Reference, fall back to the already-loaded listing row (which the table
  // proves carries them) rather than showing a blank "Direct Debit Information"
  // section. Originator Number is not carried on the listing row, so it can only
  // come from the detail op; when the gateway leaves it empty for a cancelled DD
  // there is nothing to fall back to (rendered as an em-dash, never fabricated).
  const selectedRow = directDebits.find(
    (dd) => (dd.PaymentsSeq ?? dd.PaymentId ?? '') === selectedSeq,
  );
  const detailInstitutionName =
    (selectedDD?.InstitutionName ?? '').trim() ||
    (selectedRow?.InstitutionName ?? '');
  const detailReference =
    (selectedDD?.Reference ?? '').trim() || (selectedRow?.Reference ?? '');

  // Client-side pagination of the loaded Direct Debits list (#481 client
  // windowing + #829 server pagination). At most DIRECT_DEBITS_PER_PAGE (10) rows
  // render per page; the agent moves through pages with Previous/Next. More UI
  // pages may exist beyond the loaded rows when the gateway signals `hasMore`, so
  // the current page is clamped to the greater of the loaded-row page count and
  // "one more page available". `page` is clamped defensively so a shrunk list
  // (e.g. after a cancel) can never leave the view on an out-of-range page.
  const loadedPageCount = Math.max(
    1,
    Math.ceil(directDebits.length / DIRECT_DEBITS_PER_PAGE),
  );
  const currentPage = Math.min(page, loadedPageCount - 1 + (hasMore ? 1 : 0));
  const pagedDirectDebits = directDebits.slice(
    currentPage * DIRECT_DEBITS_PER_PAGE,
    currentPage * DIRECT_DEBITS_PER_PAGE + DIRECT_DEBITS_PER_PAGE,
  );
  // "Next" is enabled while there is another LOADED page OR the gateway signals
  // more rows to fetch (#829). Disabled only on the last loaded page with no more.
  const hasNextPage = currentPage < loadedPageCount - 1 || hasMore;
  // The label shows the loaded-page count; when more remain, a trailing "+"
  // signals further pages are lazily fetchable past the loaded rows.
  const pageCount = loadedPageCount;

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="direct-debits-heading"
    >
      <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>

        {/* Email templates (#705): consolidated into a single top-right dropdown
            selector in the page header, mirroring BillPaymentsScreen (#442).
            Each template is prefilled best-effort from the in-context customer
            name and the selected account number, delivered as a formatted
            downloadable Outlook draft (`.eml`) or plain-text `mailto:` draft. */}
        <EmailTemplateMenu
          templates={DIRECT_DEBITS_TEMPLATES}
          context={{
            customerName: ownerName,
            accountNumber: account,
            content: contentMap,
          }}
        />
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="direct-debits-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
        >
          Direct Debits
        </h1>
        {/* Process-menu navigation (issue #45). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {/* Persistent payment-options selector (issue #116). Placed on the LEFT
          below the heading to match Bill Payments and the sibling payment
          screens (issue #261). */}
      <div className="mb-6 flex gap-6 flex-wrap items-start">
        <PaymentOptionsNav current="Direct Debits" />
      </div>

      {error && (
        <div
          className="mb-6 border-l-4 border-[#c81e1e] bg-[#fdeaea] p-4 max-w-[900px]"
          role="alert"
        >
          <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0">
            {error}
          </p>
        </div>
      )}

      {/* Account selector (CCF-71 / CCF-80). */}
      <div className="mb-6 max-w-[400px]">
        <Dropdown
          id="direct-debits-account"
          label="Select account to view Direct Debits"
          placeholder="Select"
          value={account}
          options={accountOptions}
          onChange={onAccountChange}
        />
      </div>

      {account && (
        <>
          {/* #799 (@dylan219, increment 3): the selected account requires more
              than one signatory ("two to sign" mandate), so Direct Debit
              cancellation is NOT permitted through the desktop. The verbatim
              block message shows inline beside the action (the per-row trashcan
              is disabled). The agent can select a different account. Enforced
              server-side too so a direct API call cannot bypass it (#173). */}
          {accountBlocksCancel && (
            <div
              role="alert"
              aria-live="polite"
              className="mb-6 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c] max-w-[1100px]"
            >
              {MULTIPLE_SIGNATORIES_MESSAGE}
            </div>
          )}

          {/* "Show Cancelled" toggle (#558/#559) — reveals the cancelled (non-active)
              Direct Debits, mirroring the Standing Orders overview toggle. */}
          <div className="mb-6">
            <Toggle
              id="show-cancelled-direct-debits-toggle"
              label="Show Cancelled"
              checked={includeInactive}
              onChange={onToggleInactive}
            />
          </div>

          {/* Direct Debits table (CCF-71). Clicking a row opens its detail
              directly — no tick-box / "View selected" step (#255). A leading
              "Status" column shows Active/Cancelled (#558/#559); a trailing
              trashcan column starts the delete-Direct-Debit flow (see below). */}
          <div className="overflow-x-auto max-w-[1100px]">
            <table className="w-full border-collapse bg-white" role="table">
              <thead>
                <tr className="bg-[#f3f4f6]">
                  {['Status', 'Institution Name', 'Reference'].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] border-b border-[#d1d5db]"
                    >
                      {h}
                    </th>
                  ))}
                  {/* Trailing action (trashcan) column — no visible header. */}
                  <th
                    className="px-4 py-3 border-b border-[#d1d5db]"
                    aria-label="Actions"
                  />
                </tr>
              </thead>
              <tbody>
                {listLoading ? (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                    >
                      Loading Direct Debits…
                    </td>
                  </tr>
                ) : directDebits.length > 0 ? (
                  pagedDirectDebits.map((dd) => {
                    const seq = dd.PaymentsSeq ?? dd.PaymentId ?? '';
                    const isSelected = selectedSeq === seq;
                    // #622: an already-cancelled Direct Debit must not offer the
                    // trashcan/delete control (the agent can't cancel a DD the
                    // gateway has already cancelled).
                    const cancelled = isDirectDebitCancelled(dd.Status);
                    return (
                      <React.Fragment key={seq}>
                        <tr
                          aria-selected={isSelected}
                          className={`border-b border-[#e5e7eb] ${
                            isSelected ? 'bg-[#f3f4f6]' : 'hover:bg-[#f9fafb]'
                          }`}
                        >
                          {/* Clicking anywhere on the data cells opens the
                              detail (#255). The clickable region is a
                              full-cell button so it is keyboard-accessible;
                              the trailing trashcan cell is a separate action
                              and does NOT open the detail. The leading Status
                              cell is NOT keyboard-tabbable (tabIndex=-1) but
                              stays in the a11y tree so it keeps "Active"/
                              "Cancelled" as its accessible name (#558/#559). */}
                          <td className="p-0">
                            <button
                              type="button"
                              disabled={detailLoading}
                              tabIndex={-1}
                              aria-hidden="true"
                              onClick={() => {
                                setSelectedSeq(seq);
                                void openDetail(seq);
                              }}
                              className="w-full h-full px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] text-[#1d252c] bg-transparent border-0 cursor-pointer disabled:cursor-not-allowed focus:outline-none"
                            >
                              {ddStatusLabel(dd.Status)}
                            </button>
                          </td>
                          <td className="p-0">
                            <button
                              type="button"
                              disabled={detailLoading}
                              aria-label={`View ${dd.InstitutionName ?? seq}`}
                              onClick={() => {
                                setSelectedSeq(seq);
                                void openDetail(seq);
                              }}
                              className="w-full h-full px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] text-[#1d252c] bg-transparent border-0 cursor-pointer disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#005fcc]"
                            >
                              {dd.InstitutionName}
                            </button>
                          </td>
                          <td className="p-0">
                            <button
                              type="button"
                              disabled={detailLoading}
                              tabIndex={-1}
                              aria-hidden="true"
                              onClick={() => {
                                setSelectedSeq(seq);
                                void openDetail(seq);
                              }}
                              className="w-full h-full px-4 py-3 text-left text-[14px] font-['Arial',sans-serif] text-[#1d252c] bg-transparent border-0 cursor-pointer disabled:cursor-not-allowed focus:outline-none"
                            >
                              {dd.Reference}
                            </button>
                          </td>
                          {/* Delete (trashcan) — starts the cancel-Direct-Debit
                              process (#255 / CCF-102 / #336). Opens a
                              confirmation DIALOG (below) rather than
                              cancelling on the first click; the actual cancel
                              routes through the RBAC + Teller-ID +
                              verification-gated /payments/direct-debit-cancel
                              op. Mirrors the sibling Bill Payments / Standing
                              Orders row-trashcan dialogs (#269/#267). */}
                          <td className="px-4 py-3 text-right w-px">
                            {!cancelled && (
                              <button
                                type="button"
                                aria-label={`Delete ${dd.InstitutionName ?? seq}`}
                                onClick={() => openCancelDialog(dd)}
                                disabled={accountBlocksCancel}
                                title={
                                  accountBlocksCancel
                                    ? MULTIPLE_SIGNATORIES_MESSAGE
                                    : undefined
                                }
                                className="p-2 rounded text-[#1D252C] hover:bg-[#f3f4f6] focus:outline-none focus:ring-2 focus:ring-[#005fcc] transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
                              >
                                <svg
                                  className="size-5"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                  aria-hidden="true"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                  />
                                </svg>
                              </button>
                            )}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-3 text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
                    >
                      No Direct Debits on this account.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Next/Previous pagination (#481 — "List lengths"). The list shows a
              maximum of DIRECT_DEBITS_PER_PAGE (10) rows per page; the agent
              pages through the loaded Direct Debits with these controls.
              Previous is disabled on the first page, Next on the last (a single
              page of ≤10 disables both). Mirrors the Standing Orders /
              Bill Payments overview pagination. */}
          {!listLoading && directDebits.length > 0 && (
            <div className="mt-6 flex items-center gap-4">
              <button
                type="button"
                disabled={currentPage <= 0}
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                aria-label="Previous direct debits page"
                className="px-8 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
              >
                &lt; Previous
              </button>
              <span
                aria-live="polite"
                className="text-[14px] font-['Arial',sans-serif] text-[#6b7280]"
              >
                {/* #829: a trailing "+" when the gateway signals further pages
                    that are fetched lazily past the currently loaded rows. */}
                Page {currentPage + 1} of {pageCount}
                {hasMore ? '+' : ''}
              </span>
              <button
                type="button"
                disabled={!hasNextPage || loadingMore}
                onClick={() => void goToNextPage()}
                aria-label="Next direct debits page"
                className="px-8 py-3 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[16px] font-['Arial',sans-serif] font-bold disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
              >
                {loadingMore ? 'Loading…' : 'Next >'}
              </button>
            </div>
          )}

          {/* Positive confirmation the Direct Debit was actually cancelled
              (CCF-102, #336) — a real gateway success only, never shown on a
              declined/forbidden attempt. */}
          {cancelSuccessMessage && (
            <div
              className="mt-6 p-4 bg-[#F8EEE3] border-l-4 border-[#FF703E] max-w-[1100px]"
              role="alert"
            >
              <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                {cancelSuccessMessage}
              </p>
            </div>
          )}

          {/* Inline detail-load error (recoverable, #103). */}
          {detailError && (
            <div
              className="mt-6 border-l-4 border-[#c81e1e] bg-[#fdeaea] p-4 max-w-[1100px]"
              role="alert"
            >
              <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                {detailError}
              </p>
            </div>
          )}

          {/* Detail panel — DD Information + Refusal Information (CCF-71/102). */}
          {selectedDD && (
            <div className="mt-8 p-8 border-2 border-[#dad7d3] bg-white max-w-[1100px]">
              <div className="flex gap-8 flex-wrap">
                {/* Direct Debit Information (CCF-102). */}
                <div>
                  <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6">
                    Direct Debit Information
                  </h2>
                  <div className="grid grid-cols-[250px_1fr] gap-y-3">
                    <DetailField
                      label="Institution Name"
                      value={detailInstitutionName}
                    />
                    <DetailField label="Reference" value={detailReference} />
                    <DetailField
                      label="Account to be debited"
                      value={selectedDD.AccountToBeDebited ?? ''}
                    />
                    <DetailField
                      label="Originator Number"
                      value={selectedDD.OriginatorNumber || '—'}
                    />
                    <DetailField
                      label="Last presented Date"
                      value={selectedDD.LastPresentedDate || '—'}
                    />
                  </div>
                </div>

                {/* Vertical divider. */}
                <div className="w-px bg-[#dad7d3] self-stretch"></div>

                {/* Refusal Information + amend/remove refusals (CCF-71). */}
                <div>
                  <h2 className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6">
                    Refusal Information
                  </h2>
                  <div className="grid grid-cols-[250px_1fr] gap-y-3">
                    <DetailField
                      label="Last Refusal Date"
                      value={selectedDD.LastRefusalDate || '—'}
                    />
                    <DetailField
                      label="Number of Refusals Requested"
                      value={selectedDD.NumOfRefusalsRequested ?? '0'}
                    />
                  </div>

                  <div className="flex items-end gap-8 mt-6">
                    <div className="w-[250px]">
                      <label
                        htmlFor="dd-refusals-input"
                        className="block text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2"
                      >
                        Number of Payments to be Refused
                      </label>
                      <input
                        id="dd-refusals-input"
                        type="text"
                        inputMode="numeric"
                        value={refusalsToBe}
                        onChange={(e) => {
                          // Mandatory; max 2 digits, max value 99 (CCF-71).
                          const digits = e.target.value
                            .replace(/\D/g, '')
                            .slice(0, 2);
                          setRefusalsToBe(digits);
                          setRefusalConfirmed(false);
                          setRefusalSummary(null);
                          setRefusalError('');
                        }}
                        placeholder="Enter number of payments"
                        aria-describedby="dd-refusals-hint"
                        className="w-full px-4 py-2 border border-[#979c9f] rounded text-[14px] font-['Arial',sans-serif] text-[#1d252c] placeholder:text-[#9ca3af] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:border-[#005fcc]"
                      />
                      <p
                        id="dd-refusals-hint"
                        className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mt-2 m-0"
                      >
                        Max 99. Enter 0 to remove refusals.
                      </p>
                    </div>
                    <div className="mb-[40px]">
                      <button
                        type="button"
                        onClick={() => void confirmRefusal()}
                        disabled={refusalSubmitting}
                        aria-label="Confirm refusal details"
                        className="px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all"
                      >
                        {refusalSubmitting ? 'Submitting…' : 'Confirm'}
                      </button>
                    </div>
                  </div>

                  {/* Inline, recoverable refusal-write failure (gateway Code +
                      Message beside the action, not only a toast — #80/82/94/141). */}
                  {refusalError && (
                    <div
                      className="mt-2 border-l-4 border-[#c81e1e] bg-[#fdeaea] p-3 max-w-[520px]"
                      role="alert"
                    >
                      <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                        {refusalError}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Refusal confirmation scripting (CCF-71) — shown on a real
                  success. Rendered from the submit-time snapshot (#378) so the
                  count reflects what was ACTUALLY done: on a removal (entered 0)
                  it reports the refusals removed (the prior count on the record),
                  NOT the "0" the agent typed. */}
              {refusalConfirmed && refusalSummary && (
                <div
                  className="p-4 bg-[#F8EEE3] border-t border-[#dad7d3] mt-6"
                  role="alert"
                >
                  <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                    {(() => {
                      const removing = refusalSummary.entered === 0;
                      const count = removing
                        ? refusalSummary.removedCount
                        : refusalSummary.entered;
                      return (
                        <>
                          Thank you{ownerName ? ` ${ownerName}` : ''}. I have{' '}
                          {removing ? 'removed the' : 'set'} {count} refusal
                          {count === 1 ? '' : 's'} on{' '}
                          {refusalSummary.institutionName} with the reference{' '}
                          {refusalSummary.reference} on your account ending{' '}
                          {refusalSummary.accountLast4}.
                        </>
                      );
                    })()}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Trashcan cancel confirmation DIALOG (CCF-102, #336). Clicking a
              row's trashcan does NOT cancel immediately — it opens this modal
              so the agent must review the Direct Debit with the customer AND
              tick the explicit "Confirm all details" consent (parity with the
              sibling Bill Payments / Standing Orders row-trashcan dialogs,
              #269/#267 — a state-changing banking action can never fire on a
              single click). Confirm posts /payments/direct-debit-cancel (RBAC
              + Teller-ID + verification gated, mapping to the shared gateway
              `payment/bill-cancel` op, PaymentType "D"); Keep dismisses it. A
              gateway decline — or an RBAC action denial — surfaces inline
              (Code + Message / the #248 denial copy) and the dialog stays
              open so the agent can retry (#87). */}
          {cancelRow && (
            <div
              className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-4"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cancel-dd-dialog-title"
            >
              <div className="w-full max-w-[520px] rounded-lg border-2 border-[#dad7d3] bg-white p-8 shadow-xl">
                <h2
                  id="cancel-dd-dialog-title"
                  className="text-[20px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4"
                >
                  Cancel Direct Debit?
                </h2>
                <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-6 m-0">
                  This will cancel the Direct Debit
                  {cancelRow.InstitutionName
                    ? ` with ${cancelRow.InstitutionName}`
                    : ''}
                  . Confirm the cancellation with the customer before proceeding
                  that they must contact the merchant as well to cancel the
                  Direct Debit. Cancelling the direct debit does not guarantee
                  that the payment will not present on the account in the
                  future.
                </p>
                {/* Explicit consent — the live cancel op is gated behind this
                    tick (CCF-102), so the trashcan can never bypass the
                    mandated confirmation. */}
                <div className="mb-6 flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="cancel-dd-confirm-details"
                    checked={cancelRowConfirmed}
                    onChange={() => setCancelRowConfirmed((c) => !c)}
                    className="size-[20px] accent-[#FF703E] cursor-pointer"
                  />
                  <label
                    htmlFor="cancel-dd-confirm-details"
                    className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                  >
                    Confirm all details
                  </label>
                </div>
                {cancelRowError && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    className="mb-4 p-3 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
                  >
                    {cancelRowError}
                  </div>
                )}
                <div className="flex gap-4">
                  <button
                    type="button"
                    disabled={cancelSubmitting || !cancelRowConfirmed}
                    onClick={() => void confirmCancelDirectDebit(cancelRow)}
                    aria-label={`Confirm cancel ${cancelRow.InstitutionName ?? 'Direct Debit'}`}
                    className="px-6 py-2.5 bg-[#FF703E] text-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {cancelSubmitting ? 'Working…' : 'Confirm cancellation'}
                  </button>
                  <button
                    type="button"
                    disabled={cancelSubmitting}
                    onClick={closeCancelDialog}
                    className="px-6 py-2.5 bg-white text-[#1D252C] border-2 border-[#1D252C] rounded-lg text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#f5f5f5] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Keep Direct Debit
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </main>
  );
}
